import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { gql, useQuery, useLazyQuery, useMutation } from '@apollo/client'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import CustomLoader from '../Loader/CustomLoader'
import DateRangeFilter from '../TableHeader/DateRangeFilter'
import { ConfirmDialog } from '../UI/RowActionMenu'
import {
  getRefundOrder,
  getPartialRefunds,
  addOrderRefund,
  deleteOrderRefund
} from '../../apollo'

const GET_REFUND_ORDER = gql`
  ${getRefundOrder}
`
const GET_PARTIAL_REFUNDS = gql`
  ${getPartialRefunds}
`
const ADD_REFUND = gql`
  ${addOrderRefund}
`
const DELETE_REFUND = gql`
  ${deleteOrderRefund}
`

const CONTROL_HEIGHT = 38

const toInputDate = date => date.toISOString().slice(0, 10)

const currentMonth = () => {
  const now = new Date()
  return {
    start: toInputDate(
      new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
    ),
    end: toInputDate(
      new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0))
    )
  }
}

const formatDate = value =>
  value ? new Date(value).toLocaleDateString('en-GB', { timeZone: 'UTC' }) : ''

// Records refunds already made in Stripe so the store report can net them
// off. Nothing here moves money.
const PartialRefunds = ({ currencySymbol = '£' }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const money = amount => `${currencySymbol}${(Number(amount) || 0).toFixed(2)}`

  const month = useMemo(currentMonth, [])
  const [startDate, setStartDate] = useState(month.start)
  const [endDate, setEndDate] = useState(month.end)

  const [orderId, setOrderId] = useState('')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [message, setMessage] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const listVariables = { startDate, endDate }
  const listState = useQuery(GET_PARTIAL_REFUNDS, {
    variables: listVariables,
    fetchPolicy: 'network-only'
  })

  const [findOrder, lookup] = useLazyQuery(GET_REFUND_ORDER, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (!data?.refundOrder)
        setMessage({ error: true, text: t('RefundOrderNotFound') })
    },
    onError: error => setMessage({ error: true, text: error.message })
  })
  const order = lookup.data?.refundOrder
  const remaining = order
    ? Math.max((order.orderAmount || 0) - (order.refundedAmount || 0), 0)
    : 0

  const refetchQueries = [
    { query: GET_PARTIAL_REFUNDS, variables: listVariables }
  ]

  const [saveRefund, { loading: saving }] = useMutation(ADD_REFUND, {
    refetchQueries,
    onCompleted: () => {
      setMessage({ error: false, text: t('RefundRecorded') })
      setAmount('')
      setReason('')
      findOrder({ variables: { orderId: order.orderId } })
    },
    onError: error => setMessage({ error: true, text: error.message })
  })

  const [removeRefund, { loading: removing }] = useMutation(DELETE_REFUND, {
    refetchQueries,
    onCompleted: () => {
      setDeleteTarget(null)
      if (order) findOrder({ variables: { orderId: order.orderId } })
    },
    onError: error => {
      setDeleteTarget(null)
      setMessage({ error: true, text: error.message })
    }
  })

  const onFind = () => {
    const id = orderId.trim()
    if (!id) return
    setMessage(null)
    findOrder({ variables: { orderId: id } })
  }

  const parsedAmount = Number(amount)
  const canSave =
    order && parsedAmount > 0 && parsedAmount <= remaining + 0.001 && !saving

  const onSave = () => {
    if (!canSave) return
    setMessage(null)
    saveRefund({
      variables: { orderId: order.orderId, amount: parsedAmount, reason }
    })
  }

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      height: CONTROL_HEIGHT,
      fontSize: 12,
      borderRadius: '6px',
      backgroundColor: theme.palette.background.primary,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.grey[300]
      }
    },
    '& .MuiInputBase-input': { color: theme.palette.text.secondary }
  }
  const labelSx = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: theme.palette.text.disabled,
    mb: 0.75
  }
  const buttonSx = {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 600,
    height: CONTROL_HEIGHT,
    px: 3,
    borderRadius: '6px'
  }
  const paperSx = {
    width: '100%',
    boxSizing: 'border-box',
    p: 3,
    mb: 3,
    borderRadius: 2,
    color: theme.palette.text.secondary
  }
  const tableSx = {
    minWidth: 720,
    '& .MuiTableCell-root': {
      fontSize: 11,
      color: theme.palette.text.secondary,
      borderColor: theme.palette.divider
    },
    '& .MuiTableHead-root .MuiTableCell-root': { fontWeight: 700 }
  }

  const field = (label, control) => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography sx={labelSx}>{label}</Typography>
      {control}
    </Box>
  )

  const refunds = listState.data?.partialRefunds ?? []

  return (
    <>
      <Paper elevation={0} sx={paperSx}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
          {t('RecordRefund')}
        </Typography>
        <Typography
          sx={{ fontSize: 12, color: theme.palette.text.disabled, mb: 2.5 }}>
          {t('PartialRefundsHelp')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 2
          }}>
          {field(
            t('OrderID'),
            <TextField
              size="small"
              value={orderId}
              onChange={event => setOrderId(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && onFind()}
              sx={{ ...inputSx, width: 220 }}
            />
          )}
          <Button
            variant="outlined"
            onClick={onFind}
            disabled={!orderId.trim() || lookup.loading}
            sx={buttonSx}>
            {t('FindOrder')}
          </Button>
        </Box>

        {order && (
          <Box
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: '6px',
              border: `1px solid ${theme.palette.divider}`
            }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 2 }}>
              {[
                [t('OrderID'), order.orderId],
                [t('StoreCol'), order.restaurantName],
                [t('Date'), formatDate(order.orderDate)],
                [t('Status'), order.orderStatus],
                [t('Paid'), money(order.orderAmount)],
                [t('Refunded'), money(order.refundedAmount)]
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography sx={labelSx}>{label}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                gap: 2
              }}>
              {field(
                `${t('RefundAmount')} (max ${money(remaining)})`,
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: remaining, step: '0.01' }}
                  value={amount}
                  onChange={event => setAmount(event.target.value)}
                  sx={{ ...inputSx, width: 180 }}
                />
              )}
              {field(
                t('RefundReason'),
                <TextField
                  size="small"
                  value={reason}
                  placeholder={t('RefundReasonPlaceholder')}
                  onChange={event => setReason(event.target.value)}
                  sx={{ ...inputSx, width: 280 }}
                />
              )}
              <Button
                variant="contained"
                disableElevation
                onClick={onSave}
                disabled={!canSave}
                sx={buttonSx}>
                {t('RecordRefund')}
              </Button>
            </Box>
          </Box>
        )}

        {message && (
          <Typography
            sx={{
              mt: 2,
              fontSize: 12,
              color: message.error
                ? theme.palette.error.main
                : theme.palette.success.main
            }}>
            {message.text}
          </Typography>
        )}
      </Paper>

      <Paper elevation={0} sx={paperSx}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 2
          }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
            {t('PartialRefunds')}
          </Typography>
          <DateRangeFilter
            size="medium"
            showClear={false}
            startDate={startDate}
            endDate={endDate}
            onChange={(nextStart, nextEnd) => {
              setStartDate(nextStart)
              setEndDate(nextEnd)
            }}
          />
        </Box>

        {listState.loading ? (
          <CustomLoader />
        ) : listState.error ? (
          <Typography sx={{ fontSize: 12, color: theme.palette.error.main }}>
            {listState.error.message}
          </Typography>
        ) : !refunds.length ? (
          <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
            {t('NoRefundsFound')}
          </Typography>
        ) : (
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('Date')}</TableCell>
                  <TableCell>{t('OrderID')}</TableCell>
                  <TableCell>{t('StoreCol')}</TableCell>
                  <TableCell align="right">{t('Paid')}</TableCell>
                  <TableCell align="right">{t('Refund')}</TableCell>
                  <TableCell>{t('RefundReason')}</TableCell>
                  <TableCell>{t('RecordedBy')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {refunds.flatMap(refundOrder =>
                  refundOrder.refunds.map(refund => (
                    <TableRow key={refund._id}>
                      <TableCell>{formatDate(refundOrder.orderDate)}</TableCell>
                      <TableCell>{refundOrder.orderId}</TableCell>
                      <TableCell>{refundOrder.restaurantName}</TableCell>
                      <TableCell align="right">
                        {money(refundOrder.orderAmount)}
                      </TableCell>
                      <TableCell align="right">
                        {money(-refund.amount)}
                      </TableCell>
                      <TableCell>{refund.reason}</TableCell>
                      <TableCell>
                        {refund.createdBy}
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: theme.palette.text.disabled
                          }}>
                          {formatDate(refund.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label={t('RemoveRefund')}
                          onClick={() =>
                            setDeleteTarget({
                              orderId: refundOrder.orderId,
                              refundId: refund._id
                            })
                          }>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`${t('RemoveRefund')}?`}
        message={t('RemoveRefundConfirm')}
        confirmLabel={t('RemoveRefund')}
        cancelLabel={t('Cancel')}
        loading={removing}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => removeRefund({ variables: deleteTarget })}
      />
    </>
  )
}

export default PartialRefunds
